import { useCallback, useEffect, useMemo } from 'react';
import { useGet } from 'hooks';
import {
  researcherListPermissions,
  organisationListPermissions,
} from 'services/api';

const usePermissions = ({ activeAlert, userLicenses, allUsersRes }) => {
  const [
    { res: fetchResearcherListPermissionsRes },
    fetchResearcherListPermissions,
  ] = useGet({ url: researcherListPermissions });
  const [
    { res: fetchOrgListPermissionsRes },
    fetchOrgListPermissions,
  ] = useGet({ url: organisationListPermissions });

  const researcherListIds = useMemo(() => {
    return [
      ...(activeAlert?.lists?.map(l => l.id) || []),
      ...(activeAlert?.excludeLists?.map(l => l.id) || []),
    ];
  }, [activeAlert?.lists, activeAlert?.excludeLists]);

  const orgListIds = useMemo(() => {
    return [
      ...(activeAlert?.organisationLists?.map(l => l.id) || []),
      ...(activeAlert?.organisationExcludeLists?.map(l => l.id) || []),
    ];
  }, [activeAlert?.organisationLists, activeAlert?.organisationExcludeLists]);

  useEffect(() => {
    if (researcherListIds.length > 0)
      fetchResearcherListPermissions({ query: { ListIds: researcherListIds } });
  }, [researcherListIds, fetchResearcherListPermissions]);

  useEffect(() => {
    if (orgListIds.length > 0)
      fetchOrgListPermissions({ query: { ListIds: orgListIds } });
  }, [orgListIds, fetchOrgListPermissions]);

  const checkUserPermissions = useCallback(
    v => {
      let haveListAccess = true;
      fetchResearcherListPermissionsRes?.forEach(p => {
        const noAccessUsers = p.accessLevels
          .filter(al => al.accessLevel === 'NoAccess')
          .map(al => al.userId);
        const accessUsers = p.accessLevels
          .filter(al => al.accessLevel !== 'NoAccess')
          .map(al => al.userId);
        haveListAccess =
          haveListAccess &&
          v.every(
            u =>
              !noAccessUsers.includes(u) &&
              (p.defaultAccess !== 'NoAccess' || accessUsers.includes(u))
          );
      });

      fetchOrgListPermissionsRes?.forEach(p => {
        const noAccessUsers = p.accessLevels
          .filter(al => al.accessLevel === 'NoAccess')
          .map(al => al.userId);
        const accessUsers = p.accessLevels
          .filter(al => al.accessLevel !== 'NoAccess')
          .map(al => al.userId);
        haveListAccess =
          haveListAccess &&
          v.every(
            u =>
              !noAccessUsers.includes(u) &&
              (p.defaultAccess !== 'NoAccess' || accessUsers.includes(u))
          );
      });

      const managerLicenses = userLicenses.map(l => l.type);
      const usersMissingManagerLicenses = allUsersRes?.userAccounts.filter(
        userAccount =>
          v.includes(userAccount.legacyUserId) &&
          !managerLicenses.every(l =>
            userAccount.licenses.some(userLicense => userLicense.type === l)
          )
      );

      if (!haveListAccess && usersMissingManagerLicenses?.length > 0) {
        return 'Selected users may not see this alert if they lack the required license(s) or access to an included list.';
      } else if (!haveListAccess) {
        return 'Selected users may not see this alert if they lack access to an included list.';
      } else if (usersMissingManagerLicenses?.length > 0) {
        return 'Selected users may not see this alert if they lack the required license(s).';
      } else {
        return '';
      }
    },
    [
      fetchResearcherListPermissionsRes,
      fetchOrgListPermissionsRes,
      userLicenses,
      allUsersRes,
    ]
  );

  return { checkUserPermissions };
};

export default usePermissions;
